import { createFileRoute } from "@tanstack/react-router";
import { LoadType } from "building-types/electrical/electricalTypesEnum";
import uid from "common/utils/uid";
import { defaultLoadCatalog } from "doc/elements/electrical/electricalTools";
import { jsxToString } from "lib/house/annotation/conversion";
import Load, { loadDefault } from "lib/house/annotation/electrical/Load";
import getPageTitle from "lib/utils/getPageTitle";
import { loadFonts } from "pdf/fonts";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { Helmet as Head } from "react-helmet-async";
import type { AABB2Weighted } from "svg/svgBoundingBoxProvider";
import { SVGBoundingBoxProvider } from "svg/svgBoundingBoxProvider";
import withAuthentication from "../../components/withAuthentication";

const SVG_PREVIEW_WIDTH = 800;
const SVG_PREVIEW_HEIGHT = 10000;
const SVG_PREVIEW_SCALE = 1;

function SVGBoundingBoxesPage() {
  function allLoadTypes(): ReactNode[] {
    const elements: ReactNode[] = [];
    const i = 200;
    let j = 100;
    for (const key in LoadType) {
      if (key === LoadType.VIRTUAL_CEILING_FAN_LIGHT) {
        continue;
      }
      elements.push(
        <Load {...loadDefault} loadDescriptor={defaultLoadCatalog[LoadType[key]]} key={uid()} x={i + 0} y={j}></Load>,
        <Load
          {...loadDefault}
          loadDescriptor={defaultLoadCatalog[LoadType[key]]}
          key={uid()}
          normal={[0.707, 0.707]}
          x={i + 100}
          y={j}
        ></Load>,
        <Load
          {...loadDefault}
          loadDescriptor={defaultLoadCatalog[LoadType[key]]}
          key={uid()}
          normal={[1, 0]}
          x={i + 200}
          y={j}
        ></Load>,
        <Load
          {...loadDefault}
          loadDescriptor={defaultLoadCatalog[LoadType[key]]}
          key={uid()}
          normal={[0.707, -0.707]}
          x={i + 300}
          y={j}
        ></Load>,
        <Load
          {...loadDefault}
          loadDescriptor={defaultLoadCatalog[LoadType[key]]}
          key={uid()}
          normal={[-1, 0]}
          x={i + 400}
          y={j}
        ></Load>,
        <Load
          {...loadDefault}
          loadDescriptor={defaultLoadCatalog[LoadType[key]]}
          key={uid()}
          normal={[-0.707, 0.707]}
          x={i + 500}
          y={j}
        ></Load>,
      );
      j += 100;
    }
    return elements;
  }

  const [bbs, setBBs] = useState<AABB2Weighted[]>([]);
  const [jsxArr, setJsxArr] = useState<ReactNode[]>([]);

  const loadFontsCallback = async () => {
    await loadFonts();
    const elements = allLoadTypes();
    setJsxArr(elements);
    const results: AABB2Weighted[] = [];
    for (const element of elements) {
      results.push(...SVGBoundingBoxProvider.instance.getAABB2sFromSVGString(jsxToString(element)));
    }

    setBBs(results);
  };

  useEffect(() => {
    void loadFontsCallback();
  }, []);

  if (!bbs || !jsxArr) {
    return <></>;
  }

  return (
    <>
      <Head>
        <title>{getPageTitle("Debug SVG Bounding Boxes")}</title>
      </Head>
      <div>
        <svg
          width={SVG_PREVIEW_WIDTH}
          height={SVG_PREVIEW_HEIGHT}
          viewBox={`${0} ${0} ${SVG_PREVIEW_WIDTH / SVG_PREVIEW_SCALE} ${SVG_PREVIEW_HEIGHT / SVG_PREVIEW_SCALE}`}
        >
          {jsxArr}
          {bbs.map((bb1, i) => (
            <rect
              fill="red"
              opacity={0.5}
              key={i}
              x={bb1.minX}
              y={bb1.minY}
              width={bb1.maxX - bb1.minX}
              height={bb1.maxY - bb1.minY}
            />
          ))}
        </svg>
      </div>
    </>
  );
}

export const Route = createFileRoute("/debug/svg-bb")({
  component: withAuthentication(SVGBoundingBoxesPage, { superAdmin: true }),
});
